import { useState } from 'react'

import { Loader } from '@mantine/core'

import { useLocation, useParams } from '@redwoodjs/router'
import { Metadata, useMutation, useQuery } from '@redwoodjs/web'

import { VoEProvider } from 'src/graphql/types/carriers'
import {
  SUBMIT_VOE_OPT_OUT,
  VALIDATE_VOE_OPT_OUT_TOKEN,
} from 'src/graphql/voeOptOut.graphql'
import { voeProviderLabel } from 'src/lib/voeProvider.utils'

import OptOutStateScreen from './components/OptOutStateScreen'
import VoEOptOutForm, {
  OPT_OUT_REASONS,
  type OptOutFormValues,
} from './components/VoEOptOutForm'

const buildDetails = (
  values: OptOutFormValues,
  carrierName: string,
  dotNumber: string | null
) => {
  const reasonLabel = OPT_OUT_REASONS.find(
    (reason) => reason.value === values.reason
  )?.label

  const platformLabel =
    values.platform === VoEProvider.OTHER
      ? values.platformOther || 'Other platform'
      : voeProviderLabel(values.platform)

  const suggestedContact = [
    values.contactName,
    values.contactEmail,
    values.contactPhone &&
      `${values.contactPhone}${values.contactPhoneIsFax ? ' (fax)' : ''}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return [
    {
      label: 'Scope',
      value:
        values.scope === 'GLOBAL'
          ? 'All employers'
          : [carrierName, dotNumber && `DOT ${dotNumber}`]
              .filter(Boolean)
              .join(' · '),
    },
    ...(reasonLabel ? [{ label: 'Reason', value: reasonLabel }] : []),
    ...(values.reasonDetail
      ? [{ label: 'Notes', value: values.reasonDetail }]
      : []),
    ...(platformLabel ? [{ label: 'Platform', value: platformLabel }] : []),
    ...(suggestedContact
      ? [{ label: 'Contact instead', value: suggestedContact }]
      : []),
  ]
}

const VoEOptOutPage = () => {
  const { search } = useLocation()
  const { voeId } = useParams()
  const token = new URLSearchParams(search).get('token') ?? ''

  const [submitted, setSubmitted] = useState<OptOutFormValues | null>(null)

  const { data, loading } = useQuery(VALIDATE_VOE_OPT_OUT_TOKEN, {
    variables: { voeId, token },
    skip: !token,
  })

  const [submitOptOut, { loading: submitting }] =
    useMutation(SUBMIT_VOE_OPT_OUT)

  const validation = data?.validateVoEOptOutToken
  const carrierName = validation?.carrierName ?? ''
  const dotNumber = validation?.dotNumber ?? null
  const maskedEmail = validation?.maskedEmail ?? ''

  const handleSubmit = async (values: OptOutFormValues) => {
    setSubmitted(values)

    await submitOptOut({
      variables: {
        input: {
          voeId,
          token,
          scope: values.scope,
          reason: values.reason || null,
          reasonDetail: values.reasonDetail || null,
          platform: values.platform || null,
          platformOther: values.platformOther || null,
          contactName: values.contactName || null,
          contactEmail: values.contactEmail || null,
          contactPhone: values.contactPhone || null,
          contactPhoneIsFax: values.contactPhoneIsFax,
        },
      },
    }).catch(() => undefined)
  }

  const screen = (() => {
    if (submitted) return 'submitted'
    if (!token) return 'expired'
    if (loading) return 'loading'
    if (!validation?.valid) return 'expired'
    if (validation.suppressedScope === 'GLOBAL') return 'already'
    return 'form'
  })()

  return (
    <>
      <Metadata
        title="Stop employment verification requests"
        description="Opt out of employment verification requests"
        robots="noindex,nofollow"
      />
      <div className="flex min-h-screen items-center justify-center bg-doubleNickel-gray-50 p-4">
        <div className="w-full max-w-lg rounded-lg border border-doubleNickel-gray-200 bg-white p-6 shadow-sm sm:p-8">
          {screen === 'loading' && (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          )}

          {screen === 'form' && (
            <VoEOptOutForm
              carrierName={carrierName}
              dotNumber={dotNumber}
              maskedEmail={maskedEmail}
              carrierAlreadySuppressed={
                validation?.suppressedScope === 'CARRIER'
              }
              canScopeToCarrier={validation?.canScopeToCarrier ?? false}
              onSubmit={handleSubmit}
              submitting={submitting}
            />
          )}

          {screen === 'submitted' && (
            <OptOutStateScreen
              tone="success"
              title="You are unsubscribed"
              details={
                submitted
                  ? buildDetails(submitted, carrierName, dotNumber)
                  : undefined
              }
              footer="Thank you — this helps us stop bothering the wrong people. You can close this page."
            >
              {submitted?.scope === 'GLOBAL' ? (
                <>
                  We will stop sending employment verification requests to{' '}
                  <span className="font-medium text-doubleNickel-gray-700">
                    {maskedEmail}
                  </span>{' '}
                  for every employer.
                </>
              ) : (
                <>
                  We will stop sending employment verification requests for{' '}
                  <span className="font-medium text-doubleNickel-gray-700">
                    {carrierName}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium text-doubleNickel-gray-700">
                    {maskedEmail}
                  </span>
                  .
                </>
              )}
            </OptOutStateScreen>
          )}

          {screen === 'expired' && (
            <OptOutStateScreen
              tone="warning"
              title="This link no longer works"
              note="To stop these requests, reply to any employment verification email you have received from us and say so. A person will action it."
              footer="Nothing has been changed."
            >
              This link is not valid, or the address on the request has since
              changed.
            </OptOutStateScreen>
          )}

          {screen === 'already' && (
            <OptOutStateScreen
              tone="info"
              title="Already taken care of"
              note="If you are still receiving requests, they are going to a different address at your company. Reply to one of them and we will stop those too."
              footer="You can close this page."
            >
              This address is already unsubscribed. No further employment
              verification requests are being sent to it.
            </OptOutStateScreen>
          )}
        </div>
      </div>
    </>
  )
}

export default VoEOptOutPage
